import { usePutFrontRichiesteIdModuliId } from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { addContextSnapshotToFormValuesFn } from '@framework/common-web/engine/engine-validation/context-snapshot/serializeContextSnapshot.ts';
import { type IEngineValidationContext } from '@framework/common-web/engine/engine-validation/IEngineValidationContext.ts';
import { ConfirmUpdateLogic } from '@framework/common-web/schema/modules/utils/confirm-update/ConfirmUpdateLogic.ts';
import { type IEngineSection } from '@framework/engine/schema/sections/IEngineSection.ts';
import { serializeFormSectionsValues } from '@framework/engine/validator/serialize/serializeFormSectionsValues.ts';
import { applyLiveContextTransform } from '@framework/validation/live/applyLiveContextTransform.ts';
import { message } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { type AnyObject, type ObjectSchema } from 'yup';
import { useSubmissionConfirmUpdate } from '../form/useSubmissionConfirmUpdate.tsx';
import {
  type IOnAfterSave,
  type IOnBeforeSave,
} from '../form/useSubmitModuleForm.ts';

/**
 * Hook che ritorna la funzione di "submit" per il salvataggio
 * in bozza di un modulo.
 */
export function useDraftSaveModuleForm(params: {
  submissionModuleId: string | null | undefined;
  submissionId: string | null | undefined;
  validationSchema: ObjectSchema<AnyObject>;
  validationContext: IEngineValidationContext;
  sections: IEngineSection[];
  onSuccess?: () => Promise<void>;
  onBeforeSave?: IOnBeforeSave;
  onAfterSave?: IOnAfterSave;
}) {
  const {
    validationSchema,
    submissionModuleId,
    submissionId,
    validationContext,
    sections,
    onSuccess,
    onAfterSave,
    onBeforeSave,
  } = params;

  const confirmUpdate = useSubmissionConfirmUpdate();

  const save = usePutFrontRichiesteIdModuliId();
  const { t } = useTranslation();

  const forceSave = usePutFrontRichiesteIdModuliId({
    request: { headers: { forceSave: true } },
  });

  const saveForm = useCallback(
    async (params: {
      submissionId: string | null | undefined;
      submissionModuleId: string | null | undefined;
      values: any;
      force: boolean;
    }) => {
      const { submissionId, submissionModuleId, values, force } = params;

      if (!submissionModuleId) {
        // biome-ignore format: <tbd>
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { submissionModuleId });
        // biome-ignore format: <tbd>
        message.error(t("error-submit-module-form.errore-salvataggio-modulo", 'Errore durante il salvataggio, ID Modulo non presente'));
        return;
      }

      if (!submissionId) {
        // biome-ignore format: <tbd>
        console.error(`[SubmitModuleForm] Impossibile salvare il modulo: id modulo o id richiesta mancanti`, { submissionId });
        // biome-ignore format: <tbd>
        message.error(t("error-submit-module-form.errore-salvataggio-richiesta", 'Errore durante il salvataggio, ID Richiesta non presente'));
        return;
      }

      const saveFn = force ? forceSave : save;

      // Anche il salvataggio in bozza materializza i semantic fields live nello snapshot:
      // il readonly usa lo snapshot salvato e non ha valori form correnti per rieseguire
      // la live context transform.
      const snapshotValidationContext = applyLiveContextTransform(
        validationSchema,
        values,
        validationContext,
      );

      const serializedValues = serializeFormSectionsValues({
        validationSchema,
        values,
        // Aggiungiamo il context snapshot ai valori serializzati
        transform: addContextSnapshotToFormValuesFn(
          sections,
          snapshotValidationContext,
        ),
      });

      await onBeforeSave?.();
      const result = await saveFn.mutateAsync({
        idRichiesta: submissionId,
        idRichiestaModulo: submissionModuleId,
        data: {
          valoriSezioni: serializedValues,
        },
      });
      await onAfterSave?.(result.richiesta);
      await onSuccess?.();
    },
    [
      forceSave,
      onAfterSave,
      onBeforeSave,
      onSuccess,
      save,
      sections,
      t,
      validationContext,
      validationSchema,
    ],
  );

  const onSubmit = useCallback(
    async (values: any) => {
      try {
        await saveForm({
          submissionId,
          submissionModuleId,
          values,
          force: false,
        });
      } catch (e: any) {
        if (ConfirmUpdateLogic.needUserConfirm(e)) {
          confirmUpdate({
            differences: e.response.data,
            onOk: async () => {
              await saveForm({
                submissionId,
                submissionModuleId,
                values,
                force: true,
              });
            },
          });
          return;
        }

        // biome-ignore format: <tbd>
        console.error(`[SubmitModuleForm] Errore durante il salvataggio`, {error: e});
        // biome-ignore format: <tbd>
        message.error(getNetworkErrorMessage(e, t("error-submit-module-form.error-saving",'Errore durante il salvataggio')));
      }
    },
    [saveForm, submissionId, submissionModuleId, t, confirmUpdate],
  );

  return onSubmit;
}

export type IUseDraftSaveModuleForm = ReturnType<typeof useDraftSaveModuleForm>;
